import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import debounce from 'lodash/debounce';

export const useDebounceStateWithPending = (init, queryFn, timer = 1000) => {
  const [value, setValue] = useState(init);
  const [pending, setPending] = useState(false);
  const lastReqIdRef = useRef(0);

  const debounced = useMemo(() => {
    const fn = debounce(async (params, reqId) => {
      try {
        const result = await queryFn(params);
        if (reqId === lastReqIdRef.current) {
          if (Array.isArray(result)) {
            setValue(result.length === 0 ? [] : result.map((el) => el?.name ?? el));
          } else {
            setValue(result);
          }
        }
      } catch (e) {
        if (reqId === lastReqIdRef.current) setValue([]);
      } finally {
        if (reqId === lastReqIdRef.current) setPending(false);
      }
    }, timer);

    return fn;
  }, [queryFn, timer]);

  const setDebounce = useCallback(
    (params) => {
      setPending(true);
      const reqId = ++lastReqIdRef.current;
      debounced(params, reqId);
    },
    [debounced],
  );

  useEffect(() => {
    return () => debounced.cancel();
  }, [debounced]);

  const flush = useCallback(() => {
    debounced.flush?.();
  }, [debounced]);

  const cancel = useCallback(() => {
    debounced.cancel?.();
    setPending(false);
  }, [debounced]);

  return [value, setDebounce, pending, { flush, cancel }];
};
